import { useState } from 'react';

import { Box, Flex, Link, Text } from '@joinrepublic/dls-ui-kit';
import { colors } from '@joinrepublic/republic-ui-levelup-theme';

import { MaybeValue, Viewer } from '@r-client/shared/data/auth';
import { Dropdown } from '@r-client/shared/ui/dropdown';
import { useAppConfig } from '@r-client/shared/util/core';

import { levelupLogoutUrl, levelupStartUrl } from '../../utils/levelup-urls';
import { ChevronDown, LogOutIcon, PortfolioIcon } from './icons';
import { MenuRow } from './menu-row';

/*
 * Raw palette entries rather than semantic tokens (`neutral.fg-primary` and friends): this menu
 * lives in the header, which renders outside `NestedLevelupThemeProvider` -- see the note in
 * `main-header.tsx` -- so a semantic token would resolve against Republic's light theme and turn
 * the white text near-black. `main-header.tsx` already reads `colors.grey['950']` for the same
 * reason. This replaces the file's private palette; three values move a shade in the process,
 * onto the brand ramp: #FF4B00 -> orange.500, #1B1B1B -> grey.900, and the two hand-rolled
 * white alphas onto the whiteAlpha scale.
 */
const LEVELUP_ACCENT = colors.orange['500'];

const LEVELUP_PANEL_BG = colors.grey['900'];
const LEVELUP_BORDER = colors.whiteAlpha['50'];
const LEVELUP_DIVIDER = colors.grey['700'];
const LEVELUP_AVATAR_BG = colors.grey['700'];
const LEVELUP_MUTED = colors.grey['400'];

interface ILevelupUserMenuProps {
  user: {
    viewer: MaybeValue<Viewer>;
    isLoading?: boolean;
  };
  /**
   * Where the branded auth pages live for this request's host: `/levelup` on the main host, and
   * the empty string on LevelUp's own, where they sit at the root. Resolved server-side and
   * passed down -- reading `window` here would differ between SSR and hydration.
   */
  authPathPrefix?: string;
}

function getDisplayName(viewer: MaybeValue<Viewer>): string {
  const info = viewer?.info as
    | { firstName?: string | null; lastName?: string | null }
    | undefined;
  const first = info?.firstName?.trim();
  const lastInitial = info?.lastName?.trim()?.[0];
  if (first && lastInitial) return `${first} ${lastInitial}.`;
  return first || 'Account';
}

function getInitials(viewer: MaybeValue<Viewer>): string {
  const info = viewer?.info as
    | { firstName?: string | null; lastName?: string | null }
    | undefined;
  const first = info?.firstName?.trim()?.[0] ?? '';
  const last = info?.lastName?.trim()?.[0] ?? '';
  // Empty when the user has neither name -- the avatar then falls back to their photo, or to
  // nothing at all, rather than a placeholder initial.
  return `${first}${last}`.toLocaleUpperCase();
}

function getEmail(viewer: MaybeValue<Viewer>): string {
  const info = viewer?.info as { email?: string | null } | undefined;
  return info?.email ?? '';
}

function getAvatarUrl(viewer: MaybeValue<Viewer>): string | null {
  const info = viewer?.info as { avatarUrl?: string | null } | undefined;
  return info?.avatarUrl?.trim() || null;
}

/**
 * The header/menu avatar, in priority order: the user's initials when they have a name (the brand
 * shows initials rather than a photo for named users), their avatar image when they have one but
 * no name, and nothing at all when they have neither -- not a placeholder circle.
 */
function UserAvatar({
  initials,
  avatarUrl,
  fontWeight,
}: {
  initials: string;
  avatarUrl: string | null;
  fontWeight: number;
}) {
  // A failed avatar load falls back to "nothing" rather than a broken-image glyph: the shared
  // `Img`/`Avatar` the Republic header uses has no such guard, so a non-loading URL (as on staging,
  // where the `theta-nauth` avatar endpoint may 404 or need a session the cross-origin request
  // can't send) would show a broken image there. Here it just disappears.
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const showImage = !initials && !!avatarUrl && !imageFailed;

  if (!initials && !showImage) return null;

  if (showImage) {
    return (
      <Box
        as="img"
        src={avatarUrl}
        alt=""
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageFailed(true)}
        boxSize="36px"
        borderRadius="18px"
        objectFit="cover"
        flexShrink={0}
        // Hidden (and out of flex layout, so no gap) until the image actually loads: a
        // never-loading avatar then reserves no space and the name never starts indented only to
        // snap left when `onError` removes it.
        display={imageLoaded ? 'block' : 'none'}
      />
    );
  }

  return (
    <Flex
      boxSize="36px"
      flexShrink={0}
      borderRadius="18px"
      bg={LEVELUP_AVATAR_BG}
      align="center"
      justify="center"
    >
      <Text
        fontWeight={fontWeight}
        color={colors.white}
        sx={{ fontSize: '14px' }}
      >
        {initials}
      </Text>
    </Flex>
  );
}

export function LevelupUserMenu({
  user: { viewer, isLoading },
  authPathPrefix = '/levelup',
}: ILevelupUserMenuProps) {
  // Same prefix the branded auth pages use: empty on LevelUp's own hostname, where those pages
  // sit at the root. Hardcoding `/levelup` put the segment back into the address bar that the
  // hostname exists to remove.
  const signInHref = `${authPathPrefix}/register/signin`;
  const applicationEnvironment = useAppConfig()?.applicationEnvironment;
  // "My portfolio" sends the signed-in investor to LevelUp through the login handshake, so they
  // land on their LevelUp portfolio already logged in (same hand-off as the post-investment
  // redirect) rather than on a logged-out LevelUp page.
  const portfolioUrl = levelupStartUrl(applicationEnvironment, '/portfolio');
  // "Log out" ends LevelUp's session and lands on their homepage; the Republic session is left
  // alone (see `levelupLogoutUrl`). A top-level link navigation, which is what LevelUp requires.
  const logoutUrl = levelupLogoutUrl(applicationEnvironment);

  if (isLoading) return null;

  // An anonymous visitor still needs a way in. The Republic header renders a "Log in" link in the
  // same situation; without this the branded header offered nothing at all.
  if (!viewer?.info) {
    return (
      <Link
        href={signInHref}
        color="white"
        fontWeight="500"
        _hover={{ textDecoration: 'none', color: LEVELUP_ACCENT }}
      >
        Log in
      </Link>
    );
  }

  const displayName = getDisplayName(viewer);
  const initials = getInitials(viewer);
  const avatarUrl = getAvatarUrl(viewer);
  const email = getEmail(viewer);

  return (
    <Dropdown
      arrow={false}
      placement="bottom-end"
      trigger="click"
      content={
        <Box
          boxSizing="border-box"
          w="217px"
          bg={LEVELUP_PANEL_BG}
          border="1px solid"
          borderColor={LEVELUP_BORDER}
          // Matches the 6px radius hardcoded on the shared `Dropdown`'s `.overlayContent`
          // wrapper (`libs/shared/ui/dropdown/src/components/dropdown/dropdown.module.scss`,
          // white background, no `overflow: hidden`). That wrapper shrink-wraps to this panel
          // exactly, so any larger radius here cuts a wider corner arc than the wrapper's -
          // leaving a sliver of the wrapper's white background visible in each corner.
          borderRadius="6px"
          px="4"
          pt="22px"
          pb="4"
          data-testid="levelup-user-menu"
        >
          <Flex
            gap="3"
            pb="3"
            borderBottom="1px solid"
            borderColor={LEVELUP_DIVIDER}
          >
            <UserAvatar
              initials={initials}
              avatarUrl={avatarUrl}
              fontWeight={700}
            />
            <Box overflow="hidden">
              <Text color={colors.white} isTruncated sx={{ fontSize: '14px' }}>
                {displayName}
              </Text>
              <Text color={LEVELUP_MUTED} isTruncated sx={{ fontSize: '13px' }}>
                {email}
              </Text>
            </Box>
          </Flex>

          {portfolioUrl ? (
            <Box py="1">
              <MenuRow
                icon={<PortfolioIcon />}
                label="My portfolio"
                href={portfolioUrl}
              />
            </Box>
          ) : null}

          <Box borderTop="1px solid" borderColor={LEVELUP_DIVIDER} pt="1">
            <MenuRow
              icon={<LogOutIcon />}
              label="Log out"
              href={logoutUrl}
              color={LEVELUP_ACCENT}
            />
          </Box>
        </Box>
      }
    >
      {/*
        Sizing/spacing here matches Figma's "Menu=Closed" header component (node `1534:31`)
        exactly: 8px padding around the whole group, 12px gap between avatar and the
        name+chevron pair, 6px gap within that pair, 36px avatar, 16px name text, 16px chevron.
        Previously this used smaller ad-hoc values (32px avatar, 14px name, 12px chevron, no
        group padding) that didn't match the open dropdown's own avatar size (36px) either.
      */}
      <Flex
        as="button"
        align="center"
        gap="3"
        p="2"
        color={colors.white}
        cursor="pointer"
        data-testid="levelup-header-avatar"
      >
        <UserAvatar
          initials={initials}
          avatarUrl={avatarUrl}
          fontWeight={600}
        />
        <Flex align="center" gap="6px">
          <Text
            fontWeight="500"
            color={colors.white}
            sx={{ fontSize: '16px', letterSpacing: '-0.08px' }}
          >
            {displayName}
          </Text>
          <ChevronDown />
        </Flex>
      </Flex>
    </Dropdown>
  );
}
